import React, { useMemo } from 'react';
import { Box, Chip, CircularProgress, MenuItem, TablePagination, TextField, Tooltip, Typography } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import { AgreementKind, RunResultsSummary, SuppliedResultRow } from '../../../../services/benchmarkService';
import { TierPill } from '../../BenchmarkPrimitives';
import { TIERS_BY_URGENCY, count, tierLabel, scenarioName } from '../../BenchmarkVocabulary';
import {
    AGREEMENT_ORDER, RESULT_PRESETS, agreementMeta, groupByCase, resultOutcomeLabel
} from './resultsVocabulary';
import { dsVar, ink } from '../../../../theme/tokens';

/** The table's filters, as kept in the page's query string. */
export interface ResultFilters {
    agreement?: string;
    outcome?: string;
    platform_tier?: string;
    scenario_id?: string;
}

export const RESULT_FILTER_KEYS: (keyof ResultFilters)[] = ['agreement', 'outcome', 'platform_tier', 'scenario_id'];

interface SuppliedResultsTableProps {
    rows: SuppliedResultRow[];
    summary: RunResultsSummary | null;
    total: number;
    page: number;
    pageSize: number;
    loading?: boolean;
    filters: ResultFilters;
    /** Replaces every filter at once; empty values clear. */
    onFiltersChange: (next: ResultFilters) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    onRowSelect: (row: SuppliedResultRow) => void;
    selectedCaseRef?: string | null;
}

const selectSx = {
    minWidth: 168,
    '& .MuiInputLabel-root': { color: dsVar('fg-2'), fontSize: '0.8125rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: dsVar('accent-fg') },
    '& .MuiOutlinedInput-root': {
        backgroundColor: dsVar('surface'), color: dsVar('fg-body'), fontSize: '0.875rem',
        '& fieldset': { borderColor: dsVar('line') },
        '&:hover fieldset': { borderColor: dsVar('line') },
        '&.Mui-focused fieldset': { borderColor: dsVar('accent-fg') }
    },
    '& .MuiSvgIcon-root': { color: dsVar('fg-muted') }
};

const headSx = {
    textAlign: 'left' as const, px: 1.5, py: 1.25, fontSize: '0.6875rem', fontWeight: 600,
    letterSpacing: '0.05em', textTransform: 'uppercase' as const, color: dsVar('fg-muted'), whiteSpace: 'nowrap' as const
};

const cellSx = { px: 1.5, py: 1.25, fontSize: '0.8125rem', color: dsVar('fg-soft'), verticalAlign: 'middle' };
const monoSx = { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.8125rem' };

export const AgreementBadge: React.FC<{ kind: AgreementKind }> = ({ kind }) => {
    const meta = agreementMeta(kind);
    return (
        <Tooltip title={meta.description} arrow>
            <Box
                component="span"
                sx={{
                    display: 'inline-block', px: 1, py: 0.3, borderRadius: '999px', fontSize: '0.6875rem',
                    fontWeight: 700, whiteSpace: 'nowrap', color: ink(meta.color),
                    backgroundColor: `${meta.color}1A`, border: `1px solid ${meta.color}4D`
                }}
            >
                {meta.label}
            </Box>
        </Tooltip>
    );
};

const COLUMNS = ['Your alert', 'Customer', 'Scenario', 'Your tier', 'Outcome', 'SAR', 'Platform tier', 'Score', 'Agreement'];

/**
 * The user's own alerts, one row each, with the tier they gave it beside the
 * tier the platform gave its case. Filterable to the disagreements that
 * matter; a row opens why its case got its tier.
 *
 * Cases with several alerts are grouped under a heading saying the tier was
 * assigned per customer case, so a shared tier is not read as a separate
 * decision about each alert.
 */
const SuppliedResultsTable: React.FC<SuppliedResultsTableProps> = ({
    rows, summary, total, page, pageSize, loading, filters, onFiltersChange,
    onPageChange, onPageSizeChange, onRowSelect, selectedCaseRef
}) => {
    const groups = useMemo(() => groupByCase(rows), [rows]);
    const activePreset = RESULT_PRESETS.find((p) =>
        filters.agreement === p.filters.agreement && filters.outcome === p.filters.outcome
        && !filters.platform_tier && !filters.scenario_id);
    const anyFilter = RESULT_FILTER_KEYS.some((k) => filters[k]);

    const setFilter = (key: keyof ResultFilters, value: string) => onFiltersChange({ ...filters, [key]: value || undefined });

    const selects: { key: keyof ResultFilters; label: string; options: { value: string; label: string }[] }[] = [
        {
            key: 'outcome',
            label: 'Outcome',
            options: [
                { value: '', label: 'Any outcome' },
                ...(summary?.outcomes ?? []).map((o) => ({ value: o, label: resultOutcomeLabel(o) }))
            ]
        },
        {
            key: 'platform_tier',
            label: 'Platform tier',
            options: [
                { value: '', label: 'Any tier' },
                ...TIERS_BY_URGENCY.map((t) => ({ value: t, label: tierLabel(t) }))
            ]
        },
        {
            key: 'scenario_id',
            label: 'Scenario',
            options: [
                { value: '', label: 'All scenarios' },
                ...(summary?.scenarios ?? []).map((s) => ({ value: s, label: scenarioName(s) }))
            ]
        }
    ];

    return (
        <Box>
            {/* One-click views of the disagreements worth looking at first. */}
            <Box className="flex flex-wrap items-center gap-2 mb-2">
                {RESULT_PRESETS.map((preset) => {
                    const active = activePreset?.key === preset.key;
                    return (
                        <Chip
                            key={preset.key}
                            label={preset.label}
                            onClick={() => onFiltersChange(active ? {} : { ...preset.filters })}
                            variant={active ? 'filled' : 'outlined'}
                            sx={{
                                fontSize: '0.75rem', fontWeight: 600,
                                color: active ? dsVar('on-accent') : dsVar('accent-fg'),
                                backgroundColor: active ? dsVar('accent') : 'transparent',
                                borderColor: 'rgba(255, 222, 89, 0.45)',
                                '&:hover': { backgroundColor: active ? '#F5D149' : 'rgba(255, 222, 89, 0.08)' }
                            }}
                        />
                    );
                })}
            </Box>

            {/* Agreement, with how many alerts fall in each — over the whole sample. */}
            <Box className="flex flex-wrap items-center gap-2 mb-3" role="group" aria-label="Filter by agreement">
                {AGREEMENT_ORDER.filter((kind) => kind !== 'not_scored' || (summary?.byAgreement.not_scored ?? 0) > 0).map((kind) => {
                    const meta = agreementMeta(kind);
                    const active = filters.agreement === kind;
                    const n = summary?.byAgreement[kind];
                    return (
                        <Chip
                            key={kind}
                            label={`${meta.label}${n !== undefined ? ` · ${count(n)}` : ''}`}
                            onClick={() => setFilter('agreement', active ? '' : kind)}
                            aria-pressed={active}
                            sx={{
                                fontSize: '0.75rem', fontWeight: 600,
                                color: active ? dsVar('on-accent') : ink(meta.color),
                                backgroundColor: active ? meta.color : `${meta.color}14`,
                                border: `1px solid ${meta.color}4D`,
                                '&:hover': { backgroundColor: active ? meta.color : `${meta.color}26` }
                            }}
                        />
                    );
                })}
            </Box>

            <Box className="flex flex-wrap items-center gap-3 mb-3">
                {selects.map((control) => (
                    <TextField
                        key={control.key}
                        select
                        size="small"
                        label={control.label}
                        value={filters[control.key] ?? ''}
                        onChange={(e) => setFilter(control.key, e.target.value)}
                        sx={selectSx}
                    >
                        {control.options.map((option) => (
                            <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.875rem' }}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                ))}
                {anyFilter && (
                    <Typography
                        component="button"
                        onClick={() => onFiltersChange({})}
                        sx={{ background: 'none', border: 0, cursor: 'pointer', fontSize: '0.8125rem', color: dsVar('fg-2'), '&:hover': { color: dsVar('accent-fg') } }}
                    >
                        Clear filters
                    </Typography>
                )}
            </Box>

            <Box sx={{
                border: `1px solid ${dsVar('line')}`, borderRadius: '8px', backgroundColor: dsVar('surface'), overflowX: 'auto',
                opacity: loading && rows.length ? 0.45 : 1, transition: 'opacity 120ms ease-out'
            }}>
                <Box component="table" sx={{ width: '100%', minWidth: 960, borderCollapse: 'collapse' }}>
                    <Box component="thead">
                        <Box component="tr" sx={{ borderBottom: `1px solid ${dsVar('line')}` }}>
                            {COLUMNS.map((c) => <Box key={c} component="th" sx={headSx}>{c}</Box>)}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {loading && rows.length === 0 && (
                            <Box component="tr">
                                <Box component="td" colSpan={COLUMNS.length} sx={{ p: 6, textAlign: 'center' }}>
                                    <CircularProgress size={24} sx={{ color: dsVar('accent-fg') }} />
                                </Box>
                            </Box>
                        )}
                        {!loading && rows.length === 0 && (
                            <Box component="tr">
                                <Box component="td" colSpan={COLUMNS.length} sx={{ p: 6, textAlign: 'center', color: dsVar('fg-muted'), fontSize: '0.875rem' }}>
                                    {anyFilter ? 'No alerts match these filters.' : 'This run has no supplied alerts to show.'}
                                </Box>
                            </Box>
                        )}
                        {groups.map((group) => {
                            const shared = group.alertsInCase > 1;
                            return (
                                <React.Fragment key={`${group.caseRef}-${group.rows[0].alertRef}`}>
                                    {shared && (
                                        <Box component="tr" sx={{ backgroundColor: dsVar('page'), borderTop: `1px solid ${dsVar('line')}` }}>
                                            <Box component="td" colSpan={COLUMNS.length} sx={{ px: 1.5, py: 0.75, fontSize: '0.75rem', color: dsVar('fg-2') }}>
                                                Customer case <Box component="span" sx={monoSx}>{group.caseRef}</Box>
                                                {' · '}{count(group.alertsInCase)} alerts · tier assigned per customer case
                                            </Box>
                                        </Box>
                                    )}
                                    {group.rows.map((row) => {
                                        const selected = Boolean(selectedCaseRef) && row.caseRef === selectedCaseRef;
                                        return (
                                            <Box
                                                key={row.alertRef}
                                                component="tr"
                                                onClick={() => onRowSelect(row)}
                                                sx={{
                                                    borderTop: `1px solid ${dsVar('line')}`, cursor: 'pointer',
                                                    backgroundColor: selected ? dsVar('raised') : 'transparent',
                                                    boxShadow: selected ? `inset 2px 0 0 ${dsVar('accent-fg')}` : shared ? `inset 2px 0 0 ${dsVar('line')}` : 'none',
                                                    '&:hover': { backgroundColor: dsVar('raised') }
                                                }}
                                            >
                                                <Box component="td" sx={{ ...cellSx, ...monoSx, color: dsVar('fg-body') }}>{row.alertRef}</Box>
                                                <Box component="td" sx={{ ...cellSx, ...monoSx }}>{row.customerRef ?? '—'}</Box>
                                                <Box component="td" sx={cellSx}>{row.scenarioId ? scenarioName(row.scenarioId) : '—'}</Box>
                                                <Box component="td" sx={cellSx}>
                                                    <Box className="flex items-center gap-1.5">
                                                        <Box component="span" sx={{ ...monoSx, color: dsVar('fg-2') }}>{row.institutionTierRaw ?? '—'}</Box>
                                                        <Box component="span" sx={{ color: dsVar('fg-faint') }}>→</Box>
                                                        {row.institutionTier
                                                            ? <TierPill code={row.institutionTier} />
                                                            : <Box component="span" sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>not mapped</Box>}
                                                    </Box>
                                                </Box>
                                                <Box component="td" sx={cellSx}>{resultOutcomeLabel(row.disposition)}</Box>
                                                <Box component="td" sx={cellSx}>{row.sarFiled === null ? '—' : row.sarFiled ? 'Yes' : 'No'}</Box>
                                                <Box component="td" sx={cellSx}>
                                                    <Box className="flex items-center gap-1">
                                                        {row.platformTier ? <TierPill code={row.platformTier} /> : '—'}
                                                        {row.hardOverride ? (
                                                            <Tooltip title={`Hard override: ${row.hardOverride === true ? 'applied' : String(row.hardOverride)}`} arrow>
                                                                <BoltIcon aria-label="Hard override" sx={{ fontSize: 16, color: ink('#F97316') }} />
                                                            </Tooltip>
                                                        ) : null}
                                                    </Box>
                                                </Box>
                                                <Box component="td" sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums' }}>{row.platformScore ?? '—'}</Box>
                                                <Box component="td" sx={cellSx}><AgreementBadge kind={row.agreement} /></Box>
                                            </Box>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </Box>
                </Box>
            </Box>

            <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={pageSize}
                rowsPerPageOptions={[25, 50, 100, 200]}
                onPageChange={(_e, next) => onPageChange(next)}
                onRowsPerPageChange={(e) => onPageSizeChange(parseInt(e.target.value, 10))}
                sx={{
                    color: dsVar('fg-2'),
                    '& .MuiTablePagination-selectIcon': { color: dsVar('fg-muted') },
                    '& .MuiIconButton-root.Mui-disabled': { color: dsVar('fg-faint') },
                    '& .MuiIconButton-root': { color: dsVar('fg-2') }
                }}
            />
        </Box>
    );
};

export default SuppliedResultsTable;
